import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Form, InputGroup, Button, Spinner, Collapse } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import { SET_MESSAGE, UPDATE_USER } from "../../Actions/Types";
import { updateUserService } from "../../Services/UserAction/UpdateUserDetails";
import { isValidPassword } from "../../Helpers/isValidPassword";
import { getUserDetailsApi } from "../../Api/UserAction/LoginActionApi";
// component to display edit password form fields
const EditPassword = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const { user, accessToken, activeLanguage } = useSelector(
    (store: StoreProptypes) => store
  );

  const getUser = (): void => {
    if (accessToken) {
      getUserDetailsApi(accessToken)
        .then((res) => {
          dispatch({
                    type: UPDATE_USER,
                    payload: { user: res?.Data },
                });
        })
        .catch((err) => {
          dispatch({
            type: SET_MESSAGE,
            payload: {
              toast_message: `${err}`,
              toast_type: "danger",
            },
          });
        });
    }
  };

  useEffect(() => {
    getUser();
  }, [accessToken, dispatch]);

  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [cPassword, setCpassword] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const [passCollapse, setPassCollapse] = useState(true);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault(); // Prevent default behavior (e.g., spacebar scrolling)
      setPassCollapse(!passCollapse);
    }
  };

  useEffect(() => {
    if (user) {
      user.name && setFullName(user.name);
      user.email && setUserEmail(user.email);
      user.company_name && setCompanyName(user.company_name);
      user.phone && setPhone(user.phone);
    }
  }, [user]);

  // function to handle form submit
  const HandleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const trimmedOtp = otp.trim();
    const isValidOTP = /^\d{6}$/.test(trimmedOtp); // e.g. 123456
    const isValidRecovery = /^[a-zA-Z0-9]{8,24}$/.test(trimmedOtp); // e.g. v24xsqHKB06WEzmo

    if (password === "") {
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t("blank password msg", { returnObjects: true }),
          toast_type: "danger",
          onclose: null,
        },
      });
    } else if (password && !isValidPassword(password)) {
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t("password-criteria", { returnObjects: true }),
          toast_type: "danger",
        },
      });
    } else if (cPassword && password !== cPassword) {
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t("Password and confirm password doesn't match", {
            returnObjects: true,
          }),
          toast_type: "danger",
        },
      });
    } else if (
      user?.is_google2fa_enabled === 1 &&
      !isValidOTP &&
      !isValidRecovery
    ) {
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t(
            "Please enter a valid 6-digit OTP or a valid recovery code."
          ),
          toast_type: "danger",
          onclose: null,
        },
      });
    } else {
      // Submit valid form
      user &&
        accessToken &&
        updateUserService(
          {
            ...user,
            name: fullName,
            phone: phone,
            email: userEmail,
            company_name: companyName,
            website: "",
          },
          accessToken,
          dispatch,
          t,
          password,
          trimmedOtp
        );
    }
  };

  return (
    <>
      <Form>
        <div className="card mt-4">
          <div className="card-header">
            <h2
              className="h6 caption caption-with-filter m-0"
              style={{ padding: "0" }}
            >
              <span
                className="caption-text d-flex justify-content-between w-100"
                onKeyDown={handleKeyDown}
                role="button"
                tabIndex={0}
                aria-expanded={passCollapse}
                onClick={() => setPassCollapse(!passCollapse)}
                style={{ cursor: "pointer", padding: "0" }}
              >
                <span>{t("Change Password")}</span>
                <span className="violations-collapse-arrow d-flex h-100">
                  {passCollapse ? (
                    <>
                      <i
                        className={`material-symbols-outlined`}
                        aria-hidden="true"
                      >
                        expand_less
                      </i>
                    </>
                  ) : (
                    <>
                      <i
                        className={`material-symbols-outlined`}
                        aria-hidden="true"
                      >
                        expand_more
                      </i>
                    </>
                  )}
                </span>
              </span>
            </h2>
          </div>
          <Collapse in={passCollapse}>
            <div className="card-body">
              <Form.Label htmlFor="password">
                {" "}
                {t("New Password")} <span className="text-danger">*</span>{" "}
              </Form.Label>
              <InputGroup className="mb-3 input-group-outline">
                <Form.Control
                  size="lg"
                  id="password"
                  type="password"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPassword(e.target.value)
                  }
                  value={password}
                />
              </InputGroup>
              <Form.Label htmlFor="cpassword">
                {" "}
                {t("Confirm Password")} <span className="text-danger">*</span>{" "}
              </Form.Label>
              <InputGroup className="mb-3 input-group-outline">
                <Form.Control
                  size="lg"
                  id="cpassword"
                  type="password"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCpassword(e.target.value)
                  }
                  value={cPassword}
                />
              </InputGroup>
              {user?.is_google2fa_enabled === 1 && (
                <>
                  {" "}
                  <Form.Label htmlFor="otp">
                    {t("Authentication Code or Recovery Code")}{" "}
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <InputGroup className="mb-3 input-group-outline">
                    <Form.Control
                      type="text"
                      aria-label="OTP"
                      placeholder={t("Enter 6-digit OTP or recovery code")}
                      onChange={(e) => setOtp(e.target.value)}
                      value={otp}
                      maxLength={24}
                      size="lg"
                    />
                  </InputGroup>
                </>
              )}
              <div className="text-center">
                <div className="text-center">
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className={activeLanguage?.is_rtl ? "me-auto" : "ms-auto"}
                    onClick={(e) => HandleSubmit(e)}
                    disabled={loading}
                  >
                    <>{t("Update Password")}</>
                  </Button>
                </div>
              </div>
            </div>
          </Collapse>
        </div>
      </Form>
    </>
  );
};
export default EditPassword;
